/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useReactiveVar } from '@apollo/client';

import {
    tenantIdVar, isAuthenticatedVar, getUserInfoVar, leftNavigationItemsVar,
    showCrossReportConfigVar, showDataReconMenusVar, showReportMenuVar,
    currentLeftMenuVar, moduleIdVar,
} from 'Apollo/config';
import { IAM_CHANGE_ORGNIZATION_NAME, IAM_CHANGE_ORGNIZATION_STATUS, UPDATE_TENANT } from 'Apollo/Mutations';
import { GET_TENANT_BY_ID } from 'Apollo/Queries';
import { signOutUser } from 'views/Auth/AuthHelper';
import { ModuleEnum } from 'constants';
import { listItems } from './utils';
import EditTenant from '../components/EditTenant';

const productName = process.env.REACT_APP_PRODUCT;

const EditTenantContainer = () => {
    const history = useHistory();
    const location = useLocation();
    const tenantVar = useReactiveVar(tenantIdVar);
    const [tenantId, setTenantId] = useState(location.state.tenantId);
    const [tenantName, setTenantName] = useState('');
    const [status, setStatus] = useState('');
    const [tenantType, setTenantType] = useState('');
    const [allowedDomains, setAllowedDomains] = useState('');
    const [onboardOrganizationAdmin, setOnboardOrganizationAdmin] = useState('');

    let shouldCheck = false;
    const [tenantNameError, setTenantNameError] = useState(false);
    const [tenantTypeError, setTenantTypeError] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    /* Apollo query to fetch tenant details by  tenant_key */
    const {
        client, data = {}, refetch,
    } = useQuery(GET_TENANT_BY_ID, {
        variables: {
            input: {
                tenantId,
            },
        },
        fetchPolicy: 'no-cache',
    });

    /* Side effect when tenant details updates */
    useEffect(() => {
        if (data && data.getTenantById) {
            setTenantName(data.getTenantById.name || '');
            setTenantType(data.getTenantById.type);
            setAllowedDomains(data.getTenantById.iamAllowedDomains || '');
            setOnboardOrganizationAdmin(data.getTenantById.iamOnboardOrganizationAdmin || '');
            setStatus(data.getTenantById.iamStatus);
        }
    }, [data]);

    /* Tenant form validation */
    const checkValidation = () => {
        if (tenantName.trim() === '') {
            setTenantNameError(true);
            shouldCheck = true;
        } else {
            setTenantNameError(false);
            shouldCheck = false;
        }

        if (tenantType.trim() === '') {
            setTenantTypeError(true);
            shouldCheck = true;
        } else {
            setTenantTypeError(false);
            shouldCheck = false;
        }
    };

    /* Apollo mutation to edit tenant details */
    const [editTenant, { loading: isEditTenantLoading }] = useMutation(UPDATE_TENANT);

    /* Apollo mutation to change IAM tenant name */
    const [iamChangeTenantName, { loading: isChangeTenantNameLoading }] = useMutation(IAM_CHANGE_ORGNIZATION_NAME, {
        variables: {
            input: {
                organizationId: data && data.getTenantById && data.getTenantById.iamOrganizationId,
                newName: tenantName,
                product: productName,
            },
        },
    });

    /* Apollo mutation to change IAM tenant name */
    const [iamChangeTenantStatus, { loading: isChangeTenantStatusLoading }] = useMutation(IAM_CHANGE_ORGNIZATION_STATUS, {
        variables: {
            input: {
                organizationId: data && data.getTenantById && data.getTenantById.iamOrganizationId,
                action: status === 'ACTIVE' ? 'ENABLE' : 'DISABLE',
                product: productName,
            },
        },
    });

    /** Calls checkValidation(), iamChangeTenantName() and editTenant() */
    const onSave = async () => {
        if (shouldCheck) {
            checkValidation();
        }
        if ((tenantName !== '' && tenantNameError === false) && (tenantType !== '' && tenantTypeError === false)) {
            setShowErrorAlert(false);
            setShowSuccessAlert(false);
            try {
                const _nameResponse = await iamChangeTenantName();
                if (_nameResponse.data.IAMchangeOrganizationName.status === 'SUCCESS') {
                    try {
                        const editRes = await editTenant({
                            variables: {
                                input: {
                                    tenantId,
                                    name: tenantName,
                                    iamStatus: status,
                                    type: tenantType,
                                },
                            },
                        });
                        if (!editRes.data.updateTenant) {
                            setErrorMsg('Error while updating Tenant in TCR');
                            setShowErrorAlert(true);
                        }
                        setSuccessMsg('Tenant Updated Successfully');
                        setShowSuccessAlert(true);
                        refetch();
                    } catch (editErr) {
                        setErrorMsg(editErr.message);
                        setShowErrorAlert(true);
                    }
                }
            } catch (_nameError) {
                setErrorMsg(_nameError.message);
                setShowErrorAlert(true);
            }
        }
    };

    /** Calls iamChangeTenantStatus() and editTenant() */
    const changeStatus = async () => {
        if (status && data && data.getTenantById && (status !== data.getTenantById.iamStatus)) {
            try {
                const _statusResponse = await iamChangeTenantStatus();
                if (_statusResponse.data.IAMchangeOrganizationStatus.status === 'SUCCESS') {
                    try {
                        const editTenantRes = await editTenant({
                            variables: {
                                input: {
                                    tenantId,
                                    name: tenantName,
                                    iamStatus: status,
                                    type: tenantType,
                                },
                            },
                        });
                        if (!editTenantRes.data.updateTenant) {
                            setErrorMsg('Error while updating Tenant in TCR');
                            setShowErrorAlert(true);
                        }
                        if (tenantVar === tenantId && status === 'INACTIVE') {
                            signOutUser();
                            client.clearStore();
                            isAuthenticatedVar(false);
                            getUserInfoVar({});
                            history.push('/login');
                        } else {
                            refetch();
                            setSuccessMsg('Tenant status updated successfully!');
                            setShowSuccessAlert(true);
                        }
                    } catch (editTenantErr) {
                        setErrorMsg(editTenantErr.message);
                        setShowErrorAlert(true);
                    }
                }
            } catch (_statusError) {
                setErrorMsg(_statusError.message);
                setShowErrorAlert(true);
            }
        }
    };

    useEffect(() => {
        leftNavigationItemsVar(listItems);
        showCrossReportConfigVar(false);
        showDataReconMenusVar(false);
        showReportMenuVar(false);
        currentLeftMenuVar(ModuleEnum.TENANT);
    }, []);

    /* Side effect when location updates */
    useEffect(() => {
        setTenantId(location.state.tenantId);
    }, [location]);

    /* Side effect when status updates */
    useEffect(async () => {
        if (status === 'INACTIVE' && status !== data.getTenantById.iamStatus) {
            setShowWarningModal(true);
        } else {
            changeStatus();
        }
    }, [status, data]);

    /* Side effect when confirmDelete updates */
    useEffect(() => {
        if (confirmDelete) {
            changeStatus();
        } else {
            setStatus(data && data.getTenantById && data.getTenantById.iamStatus);
        }
    }, [confirmDelete]);

    /* Breadcrumb items configuration */
    const breadcrumbItems = [
        {
            link: '',
            value: (
                <span
                    className="appkiticon icon-home-fill font-16"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.push('/');
                            showCrossReportConfigVar(false);
                            leftNavigationItemsVar([]);
                            moduleIdVar(null);
                        }
                    }
                    role="presentation"
                />
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text" onClick={() => history.goBack()} role="presentation">Tenant Management</span>,
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text">Edit Tenant</span>,
        },
    ];

    return (
        <EditTenant
            history={history}
            breadcrumbItems={breadcrumbItems}
            checkValidation={checkValidation}
            onSave={onSave}
            isChangeTenantNameLoading={isChangeTenantNameLoading}
            isChangeTenantStatusLoading={isChangeTenantStatusLoading}
            isEditTenantLoading={isEditTenantLoading}
            showErrorAlert={showErrorAlert}
            setShowErrorAlert={setShowErrorAlert}
            errorMsg={errorMsg}
            tenantName={tenantName}
            setTenantName={setTenantName}
            tenantNameError={tenantNameError}
            setTenantType={setTenantType}
            tenantType={tenantType}
            tenantTypeError={tenantTypeError}
            status={status}
            setStatus={setStatus}
            onboardOrganizationAdmin={onboardOrganizationAdmin}
            setOnboardOrganizationAdmin={setOnboardOrganizationAdmin}
            allowedDomains={allowedDomains}
            setAllowedDomains={setAllowedDomains}
            successMsg={successMsg}
            showSuccessAlert={showSuccessAlert}
            setShowSuccessAlert={setShowSuccessAlert}
            showWarningModal={showWarningModal}
            setShowWarningModal={setShowWarningModal}
            setConfirmDelete={setConfirmDelete}
        />
    );
};

export default EditTenantContainer;
